import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import StatCard from "@/components/StatCard";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate, fmtDateTime, fmtINR } from "@/lib/format";
import { ArrowLeft, Mail, MessageCircle, MapPin, User, Pencil, MessageSquare, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";

import { useRoleBase } from "@/lib/roles";

export default function SupplierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const base = useRoleBase();
  const [data, setData] = useState(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactForm, setContactForm] = useState({ email: "", phone: "" });
  const [confirmEmail, setConfirmEmail] = useState(false);
  const [savingContact, setSavingContact] = useState(false);

  const reload = () => api.get(`/suppliers/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Supplier not found"));

  const saveContact = async () => {
    const emailChanged = contactForm.email.trim().toLowerCase() !== (data?.supplier?.email || "").toLowerCase();
    if (emailChanged && !confirmEmail) { setConfirmEmail(true); return; }
    setSavingContact(true);
    try {
      await api.patch(`/suppliers/${id}/contact`, { email: contactForm.email.trim(), phone: contactForm.phone.trim() });
      toast.success("Contact details updated — change recorded in the audit log");
      setContactOpen(false);
      reload();
    } catch (e) { toast.error(formatApiError(e)); } finally { setSavingContact(false); }
  };

  useEffect(() => {
    api.get(`/suppliers/${id}`).then((r) => setData(r.data)).catch(() => toast.error("Supplier not found"));
  }, [id]);

  if (!data) return <div className="py-20 text-center text-sm text-muted-foreground">Loading supplier…</div>;
  const { supplier: s, pos, communications } = data;
  const m = s.metrics;

  return (
    <div data-testid="supplier-detail-page" className="space-y-4">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" data-testid="back-btn" onClick={() => navigate(`${base}/suppliers`)}><ArrowLeft className="h-4 w-4" /></Button>
        <div>
          <h1 className="font-heading text-2xl font-semibold tracking-tight">{s.name}</h1>
          <p className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{s.city}</span>
            <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" />{s.contact_person}</span>
            <span className="flex items-center gap-1"><Mail className="h-3.5 w-3.5" />{s.email}</span>
            <span className="flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" /><span className="font-mono">{s.phone}</span></span>
            {s.gst_number && <span data-testid="supplier-gst" className="flex items-center gap-1 font-mono text-xs">GST: {s.gst_number}</span>}
          </p>
        </div>
        <div className="ml-auto flex shrink-0 gap-2">
          <Button variant="outline" size="sm" data-testid="message-seller-btn" disabled={!s.supplier_company_id}
            onClick={() => navigate(`${base}/chat?seller=${s.supplier_company_id}`)}>
            <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Message Seller
          </Button>
          <Button variant="outline" size="sm" data-testid="edit-contact-btn"
            onClick={() => { setContactForm({ email: s.email, phone: s.phone }); setConfirmEmail(false); setContactOpen(true); }}>
            <Pencil className="mr-1.5 h-3.5 w-3.5" /> Edit Contact Details
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <StatCard label="Total POs" value={m.total_pos} testId="sup-total" />
        <StatCard label="Active" value={m.active_pos} tone="blue" testId="sup-active" />
        <StatCard label="Delayed" value={m.delayed_pos} tone="red" testId="sup-delayed" />
        <StatCard label="On-Time Delivery" value={m.on_time_pct != null ? `${m.on_time_pct}%` : "—"} tone="green" testId="sup-ontime" />
        <StatCard label="Avg Response" value={m.avg_response_hours != null ? `${m.avg_response_hours}h` : "—"} tone="amber" testId="sup-response" />
      </div>

      {s.master && (
        <div className="rounded-lg border bg-card" data-testid="supplier-master-data">
          <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Supplier Information</h2>
          <dl className="grid grid-cols-1 gap-x-10 px-5 py-2 sm:grid-cols-2">
            {[
              ["Company", s.master.name],
              ["Supplier Code", s.master.seller_code || s.supplier_code],
              ["Contact Person", s.master.contact_person],
              ["Email", s.master.email],
              ["Contact Number", s.master.phone],
              ["Annual Revenue", s.master.annual_revenue ? `${s.master.annual_revenue.currency === "INR" ? "₹" : s.master.annual_revenue.currency + " "}${Number(s.master.annual_revenue.amount).toLocaleString("en-IN")}` : null],
              ["Category", (s.master.categories || []).join(", ")],
              ["MSME", s.master.msme_registered ? `Yes${s.master.udyam_number ? ` · Udyam: ${s.master.udyam_number}` : ""}` : "No"],
              ["PAN", s.master.pan],
              ["GST", s.master.gst_number || s.gst_number],
              ["Address", s.master.address],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between gap-4 border-b py-2.5 text-sm last:border-0 sm:[&:nth-last-child(2)]:border-0">
                <dt className="text-muted-foreground">{k}</dt>
                <dd className="text-right font-medium" data-testid={`master-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v || "—"}</dd>
              </div>
            ))}
          </dl>
          {s.master.bank && Object.values(s.master.bank).some(Boolean) && (
            <div className="border-t px-5 py-4" data-testid="supplier-bank-details">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Bank Account Details</p>
              <dl className="grid grid-cols-1 gap-x-10 sm:grid-cols-2">
                {[
                  ["Bank Name", s.master.bank.bank_name],
                  ["Account Holder", s.master.bank.account_holder_name],
                  ["Account Number", s.master.bank.account_number],
                  ["IFSC Code", s.master.bank.ifsc_code],
                  ["Branch", s.master.bank.branch_name],
                ].map(([k, v]) => (
                  <div key={k} className="flex items-center justify-between gap-4 py-1.5 text-sm">
                    <dt className="text-muted-foreground">{k}</dt>
                    <dd className="font-mono text-xs font-medium" data-testid={`bank-${k.toLowerCase().replace(/\s+/g, "-")}`}>{v || "—"}</dd>
                  </div>
                ))}
              </dl>
            </div>
          )}
        </div>
      )}

      <Dialog open={contactOpen} onOpenChange={(v) => { setContactOpen(v); if (!v) setConfirmEmail(false); }}>
        <DialogContent className="bg-white sm:max-w-md" data-testid="edit-contact-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Edit Contact Details</DialogTitle>
            <DialogDescription>Updates the seller's business contact for your organization. The seller's login identity is not affected.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email ID</Label>
              <Input data-testid="edit-contact-email" type="email" className="mt-1.5" value={contactForm.email}
                onChange={(e) => { setContactForm({ ...contactForm, email: e.target.value }); setConfirmEmail(false); }} />
            </div>
            <div>
              <Label>Contact Number</Label>
              <Input data-testid="edit-contact-phone" className="mt-1.5" value={contactForm.phone}
                onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })} />
            </div>
            {confirmEmail && (
              <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900" data-testid="edit-contact-confirm">
                Changing the Seller email will affect future event and communication notifications. Click Save again to confirm.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactOpen(false)}>Cancel</Button>
            <Button data-testid="edit-contact-save" disabled={savingContact} onClick={saveContact} className="bg-slate-900 hover:bg-slate-800">
              {savingContact && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {confirmEmail ? "Confirm & Save" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="rounded-lg border bg-card">
        <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Purchase Orders</h2>
        <table className="w-full text-sm" data-testid="supplier-pos-table">
          <thead>
            <tr className="border-b text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-5 py-2.5 font-medium">PO Number</th>
              <th className="px-5 py-2.5 font-medium">Item</th>
              <th className="px-5 py-2.5 font-medium text-right">Value</th>
              <th className="px-5 py-2.5 font-medium">Expected Delivery</th>
              <th className="px-5 py-2.5 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {pos.map((p) => (
              <tr key={p.id} data-testid={`supplier-po-${p.po_number}`} onClick={() => navigate(`${base}/pos/${p.id}`)} className="cursor-pointer border-b last:border-0 hover:bg-slate-50">
                <td className="px-5 py-3 font-mono font-medium">{p.po_number}</td>
                <td className="max-w-64 truncate px-5 py-3 text-muted-foreground">{p.item}</td>
                <td className="px-5 py-3 text-right font-mono">{fmtINR(p.value)}</td>
                <td className="px-5 py-3">{fmtDate(p.expected_delivery_date)}</td>
                <td className="px-5 py-3"><StatusBadge status={p.status} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-lg border bg-card">
        <h2 className="border-b px-5 py-3 font-heading text-base font-semibold">Recent Communication</h2>
        <div className="divide-y">
          {communications.length === 0 && <p className="p-5 text-sm text-muted-foreground">No communication history.</p>}
          {communications.map((c) => (
            <div key={c.id} className="px-5 py-3">
              <p className="text-xs text-muted-foreground">
                <span className="font-mono">{c.po_number}</span> · {c.sender_name} · {c.channel} · <span className="font-mono">{fmtDateTime(c.created_at)}</span>
              </p>
              <p className="mt-1 text-sm">{c.message}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
